import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import SafeHtml from "./SafeHtml";
import { Warning } from "@phosphor-icons/react";

const PALETTE = ["#0055FF", "#059669", "#7C3AED", "#D97706", "#0EA5E9", "#DC2626", "#0A0A0A", "#DB2777", "#16A34A", "#9333EA"];

export const fmtMoney = (v) => "$" + Math.round(Number(v) || 0).toLocaleString();
export const fmtNum = (v) => (Number(v) || 0).toLocaleString();
const fmtCell = (v, type) => (type === "money" ? fmtMoney(v) : type === "number" ? fmtNum(v) : (v ?? "—"));

// minimal, safe markdown → html for text widgets
function mdToHtml(md) {
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inline = (s) => esc(s).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/`(.+?)`/g, "<code>$1</code>");
  const out = []; let list = null;
  const close = () => { if (list) { out.push(`</${list}>`); list = null; } };
  for (const raw of (md || "").split("\n")) {
    const line = raw.trimEnd(); let m;
    if ((m = line.match(/^#{1,6}\s+(.*)/))) { close(); out.push(`<h4>${inline(m[1])}</h4>`); }
    else if ((m = line.match(/^\s*[-*]\s+(.*)/))) { if (list !== "ul") { close(); list = "ul"; out.push("<ul>"); } out.push(`<li>${inline(m[1])}</li>`); }
    else if ((m = line.match(/^\s*\d+[.)]\s+(.*)/))) { if (list !== "ol") { close(); list = "ol"; out.push("<ol>"); } out.push(`<li>${inline(m[1])}</li>`); }
    else if (line.trim() === "") { close(); }
    else { close(); out.push(`<p>${inline(line)}</p>`); }
  }
  close();
  return out.join("");
}

const Shell = ({ title, full, children, testid }) => (
  <div data-testid={testid} className={`flex flex-col rounded-sm border border-neutral-200 bg-white p-5 shadow-sm ${full ? "lg:col-span-2" : ""}`}>
    {title && <h3 className="mb-3 font-heading text-base font-black tracking-tight text-[#0A0A0A]">{title}</h3>}
    <div className="flex-1">{children}</div>
  </div>
);

function StatCard({ w, d }) {
  const val = d?.value;
  const display = d?.money ? fmtMoney(val) : typeof val === "number" ? fmtNum(val) : (val ?? "—");
  return (
    <Shell testid={`widget-${w.id}`}>
      <p className="text-xs uppercase tracking-widest font-semibold text-neutral-500">{w.title || d?.label}</p>
      <p className="mt-2 font-heading text-4xl font-black tracking-tighter text-[#0A0A0A]">{display}</p>
    </Shell>
  );
}

function BarW({ w, d }) {
  const x = w.x || d?.x; const y = w.y || d?.y;
  return (
    <Shell testid={`widget-${w.id}`} title={w.title} full={w.width === "full"}>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={d.rows} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
          <XAxis dataKey={x} tick={{ fontSize: 11 }} angle={-30} textAnchor="end" interval={0} height={60} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip formatter={(v) => fmtNum(v)} />
          <Bar dataKey={y} radius={[3, 3, 0, 0]}>
            {d.rows.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Shell>
  );
}

function LineW({ w, d }) {
  const x = w.x || d?.x; const y = w.y || d?.y;
  return (
    <Shell testid={`widget-${w.id}`} title={w.title} full={w.width !== "half"}>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={d.rows} margin={{ top: 5, right: 20, left: 0, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
          <XAxis dataKey={x} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip formatter={(v) => fmtNum(v)} />
          <Line type="monotone" dataKey={y} stroke="#0055FF" strokeWidth={2.5} dot={{ r: 3 }} />
        </LineChart>
      </ResponsiveContainer>
    </Shell>
  );
}

function PieW({ w, d }) {
  const x = w.x || d?.x; const y = w.y || d?.y;
  return (
    <Shell testid={`widget-${w.id}`} title={w.title}>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie data={d.rows} dataKey={y} nameKey={x} cx="50%" cy="50%" outerRadius={90} label={(e) => e[x]}>
            {d.rows.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
          </Pie>
          <Tooltip formatter={(v) => fmtNum(v)} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
        </PieChart>
      </ResponsiveContainer>
    </Shell>
  );
}

function TableW({ w, d }) {
  const cols = d.columns || [];
  return (
    <Shell testid={`widget-${w.id}`} title={w.title} full>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-neutral-200 bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              {cols.map((c) => <th key={c.key} className={`px-3 py-2.5 ${c.type === "money" || c.type === "number" ? "text-right" : "text-left"}`}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {d.rows.map((r, i) => (
              <tr key={i} className="border-b border-neutral-100 even:bg-neutral-50/50">
                {cols.map((c) => <td key={c.key} className={`px-3 py-2.5 ${c.type === "money" || c.type === "number" ? "text-right font-mono" : "text-neutral-700"}`}>{fmtCell(r[c.key], c.type)}</td>)}
              </tr>
            ))}
            {d.rows.length === 0 && <tr><td colSpan={cols.length} className="px-3 py-6 text-center text-neutral-400">No data for this range.</td></tr>}
          </tbody>
        </table>
      </div>
    </Shell>
  );
}

function TextW({ w }) {
  return (
    <Shell testid={`widget-${w.id}`} title={w.title} full>
      <SafeHtml html={mdToHtml(w.content)} className="prose-asset text-sm text-neutral-700" />
    </Shell>
  );
}

export default function ReportWidget({ w, data }) {
  if (w.type === "text") return <TextW w={w} />;
  const d = data?.[w.id];
  if (!d || d.kind === "error")
    return (
      <Shell testid={`widget-${w.id}`} title={w.title}>
        <div className="flex h-32 items-center justify-center gap-2 text-sm text-[#D97706]"><Warning size={18} weight="fill" /> {d?.message || "No data."}</div>
      </Shell>
    );
  if (w.type === "stat") return <StatCard w={w} d={d} />;
  if (w.type === "bar") return <BarW w={w} d={d} />;
  if (w.type === "line") return <LineW w={w} d={d} />;
  if (w.type === "pie") return <PieW w={w} d={d} />;
  if (w.type === "table") return <TableW w={w} d={d} />;
  return null;
}
